import { defineStore } from 'pinia'
import { removeParcelDimension, saveParcelDimensions } from '@/api/orders'

// Structure d'un article de déclaration douanière
export interface CustomsArticle {
  brand?: string
  reference?: string
  category?: string
  description: string
  quantity: number
  weight: number
  hsCode: string
  value: number
  origin: string
}

// Structure pour les dimensions physiques d'un colis
export interface PhysicalParcelData {
  longueur: number | null
  largeur: number | null
  hauteur: number | null
  poids: number | null  // poids physique du colis
}

// Structure pour l'ensemble des dimensions physiques
export interface ParcelDimensionsData {
  dimensions: PhysicalParcelData[]
  nbColis: number
}

// Structure pour une commande : articles par colis + dimensions physiques
export interface CustomsDeclarationData {
  // Articles par colis (tableau de tableaux)
  articlesByColis: CustomsArticle[][]
  // Poids des colis (pour déclaration douanière)
  colisPoids: number[]
  // Informations facture (pour Easy)
  invoiceNumber?: string
  invoiceDate?: string
  // Les déclarations globales récentes saisissent une valeur totale par ligne.
  valueMode?: 'total'
  // AJOUT : Dimensions physiques des colis
  parcelDimensions?: ParcelDimensionsData
}

// Structure complète stockée : commandeId -> données
interface CustomsDeclarations {
  [orderId: number]: CustomsDeclarationData
}

const STORAGE_KEY = 'efashion_customs_declarations'

// Fonction pour charger depuis localStorage
const loadFromStorage = (): CustomsDeclarations => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    return {}
  }
}

// Fonction pour sauvegarder dans localStorage
const saveToStorage = (data: CustomsDeclarations) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    // Erreur silencieuse lors de la sauvegarde
  }
}

export const useCustomsDeclarationStore = defineStore('customsDeclaration', {
  state: () => ({
    declarations: loadFromStorage()
  }),

  getters: {
    // Récupérer les données d'une commande
    getDeclaration: (state) => (orderId: number): CustomsDeclarationData | null => {
      return state.declarations[orderId] || null
    },

    // Vérifier si une commande a des données sauvegardées
    hasDeclaration: (state) => (orderId: number): boolean => {
      return !!state.declarations[orderId]
    },

    // RÉCUPÉRER LES DIMENSIONS PHYSIQUES
    getParcelDimensions: (state) => (orderId: number): PhysicalParcelData[] => {
      const declaration = state.declarations[orderId]
      return declaration?.parcelDimensions?.dimensions || []
    },

    // RÉCUPÉRER LE NOMBRE DE COLIS
    getNbColis: (state) => (orderId: number): number => {
      const declaration = state.declarations[orderId]
      return declaration?.parcelDimensions?.nbColis || 1
    },

    // VÉRIFIER SI DES DIMENSIONS PHYSIQUES SONT SAUVEGARDÉES
    hasParcelDimensions: (state) => (orderId: number): boolean => {
      const declaration = state.declarations[orderId]
      return !!(declaration?.parcelDimensions?.dimensions?.length)
    }
  },

  actions: {
    // Sauvegarder les données d'une commande
    saveDeclaration(orderId: number, data: CustomsDeclarationData) {
      this.declarations[orderId] = { ...data }
      saveToStorage(this.declarations)
    },

    // Mettre à jour les articles d'une commande
    updateArticles(orderId: number, articlesByColis: CustomsArticle[][], valueMode?: 'total') {
      if (!this.declarations[orderId]) {
        this.declarations[orderId] = {
          articlesByColis: [],
          colisPoids: []
        }
      }
      this.declarations[orderId].articlesByColis = articlesByColis.map(colis => 
        colis.map(article => ({ ...article }))
      )
      this.declarations[orderId].valueMode = valueMode
      saveToStorage(this.declarations)
    },

    // Mettre à jour les poids des colis
    updateColisPoids(orderId: number, colisPoids: number[]) {
      if (!this.declarations[orderId]) {
        this.declarations[orderId] = {
          articlesByColis: [],
          colisPoids: []
        }
      }
      this.declarations[orderId].colisPoids = [...colisPoids]
      saveToStorage(this.declarations)
    },

    // Mettre à jour les infos facture (pour Easy)
    updateInvoiceInfo(orderId: number, invoiceNumber: string, invoiceDate: string) {
      if (!this.declarations[orderId]) {
        this.declarations[orderId] = {
          articlesByColis: [],
          colisPoids: []
        }
      }
      this.declarations[orderId].invoiceNumber = invoiceNumber
      this.declarations[orderId].invoiceDate = invoiceDate
      saveToStorage(this.declarations)
    },

    // Supprimer les données d'une commande (après génération réussie)
    clearDeclaration(orderId: number) {
      if (this.declarations[orderId]) {
        delete this.declarations[orderId]
        saveToStorage(this.declarations)
      }
    },

    // SAUVEGARDER LES DIMENSIONS PHYSIQUES D'UNE COMMANDE
    saveParcelDimensions(orderId: number, dimensions: PhysicalParcelData[]) {
      if (!this.declarations[orderId]) {
        this.declarations[orderId] = {
          articlesByColis: [],
          colisPoids: []
        }
      }
      this.declarations[orderId].parcelDimensions = {
        dimensions: dimensions.map(dim => ({ ...dim })),
        nbColis: dimensions.length
      }
      saveToStorage(this.declarations)
    },

    // METTRE À JOUR UNE DIMENSION SPÉCIFIQUE D'UN COLIS
    async updateParcelDimension(orderId: number, colisIndex: number, field: keyof PhysicalParcelData, value: number | null) {
      if (!this.declarations[orderId]?.parcelDimensions?.dimensions) {
        return
      }

      const dimensions = this.declarations[orderId].parcelDimensions!.dimensions
      if (colisIndex >= 0 && colisIndex < dimensions.length) {
        dimensions[colisIndex] = {
          ...dimensions[colisIndex],
          [field]: value
        }
        saveToStorage(this.declarations)
      }
    },

    // AJOUTER UN COLIS
    async addParcel(orderId: number) {
      if (!this.declarations[orderId]) {
        this.declarations[orderId] = {
          articlesByColis: [],
          colisPoids: []
        }
      }

      if (!this.declarations[orderId].parcelDimensions) {
        this.declarations[orderId].parcelDimensions = {
          dimensions: [],
          nbColis: 0
        }
      }

      // Ajouter un nouveau colis vide
      this.declarations[orderId].parcelDimensions!.dimensions.push({
        longueur: null,
        largeur: null,
        hauteur: null,
        poids: null
      })

      this.declarations[orderId].parcelDimensions!.nbColis++
      saveToStorage(this.declarations)
    },

    // SUPPRIMER UN COLIS
    async removeParcel(orderId: number, colisIndex: number) {
      const parcelDimensions = this.declarations[orderId]?.parcelDimensions
      if (!parcelDimensions?.dimensions || parcelDimensions.dimensions.length <= 1) {
        return
      }

      if (colisIndex >= 0 && colisIndex < parcelDimensions.dimensions.length) {
        await removeParcelDimension(orderId, colisIndex)
        parcelDimensions.dimensions.splice(colisIndex, 1)
        parcelDimensions.nbColis--
        saveToStorage(this.declarations)
      }
    },

    // INITIALISER LES DIMENSIONS POUR UNE COMMANDE
    initializeParcelDimensions(orderId: number, nbColis = 1) {
      if (!this.declarations[orderId]) {
        this.declarations[orderId] = {
          articlesByColis: [],
          colisPoids: []
        }
      }

      this.declarations[orderId].parcelDimensions = {
        dimensions: Array.from({ length: nbColis }, () => ({
          longueur: null,
          largeur: null,
          hauteur: null,
          poids: null
        })),
        nbColis
      }

      saveToStorage(this.declarations)
    },

    // SAUVEGARDER LES DIMENSIONS DANS LA BASE DE DONNÉES
    async saveDimensionsToDB(orderId: number): Promise<boolean> {
      const dimensions = this.getParcelDimensions(orderId)
      if (dimensions.length === 0) return false

      // Ne sauvegarder que si toutes les dimensions sont complètes
      const allComplete = dimensions.every(
        (d) =>
          d.longueur != null && d.longueur > 0 &&
          d.largeur != null && d.largeur > 0 &&
          d.hauteur != null && d.hauteur > 0 &&
          d.poids != null && d.poids > 0
      )
      if (!allComplete) return false

      // Sauvegarder via l'API
      await saveParcelDimensions(orderId, dimensions)
      return true
    },

    // CHARGER LES DIMENSIONS (cache local uniquement)
    async loadParcelDimensionsFromDB(orderId: number) {
      try {
        return this.getParcelDimensions(orderId)
      } catch (error) {
        return []
      }
    },

    // SUPPRIMER LES DIMENSIONS PHYSIQUES D'UNE COMMANDE
    clearParcelDimensions(orderId: number) {
      if (this.declarations[orderId]?.parcelDimensions) {
        delete this.declarations[orderId].parcelDimensions
        saveToStorage(this.declarations)
      }
    },

    // Supprimer toutes les déclarations (utile pour le debug)
    clearAll() {
      this.declarations = {}
      saveToStorage(this.declarations)
    }
  }
})
